import { useEffect, useState } from 'react';

import Container from '@mui/material/Container';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { requestAll } from '../../../apis/config';
import { fileManagerApis } from '../../../apis/fileManagerApis';
import { solrApis } from '../../../apis/solrApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import Editor from '../../../components/page-sections/editor/Editor';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { handleErrors } from '../../../helpers';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { PRIVATE_ROUTE } from '../../../routes/routes';
import { getFileFormData, sentenceCase } from '../../../utils/utils';

const SolrFileEditorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [instance, setInstance] = useState(null);
  const [error, setError] = useState(false);
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(true);

  const state = location.state;
  const path = state.folderChain.reduce(
    (prev, curr) => `${prev}/${curr.name}`,
    []
  );

  const isXml =
    state?.file?.ext === 'xml' || state?.file?.name === 'managed-schema';
  const mode = isXml ? 'xml' : state?.file?.ext;

  useEffect(() => {
    if (isEmpty(state)) {
      // Since the state is empty, there are two possibilities:
      // If the user tries to access the page directly, redirect to the projects page, otherwise go back
      const isHistoryStackEmpty = location.key === 'default';
      isHistoryStackEmpty ? navigate(PRIVATE_ROUTE.PROJECTS) : navigate(-1);
      return;
    }

    const fetchFile = async () => {
      setLoading(true);
      const {
        file: { name },
        subscript,
      } = state;

      try {
        const [instanceRes, fileRes] = await requestAll([
          solrApis.getInstance(projectId, instanceId),
          fileManagerApis.downloadFiles(
            projectId,
            instanceId,
            false,
            subscript,
            path,
            name
          ),
        ]);

        setInstance(instanceRes.data);
        if (typeof fileRes.data !== 'string' && mode !== 'json') {
          setCode(JSON.stringify(fileRes.data, null, 4));
        } else {
          setCode(fileRes.data);
        }
      } catch (error) {
        handleErrors(navigate, error, showToast);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchFile();
  }, [projectId, instanceId]);

  const onSubmit = async (mode, code) => {
    const getFile = (mode) => {
      switch (mode) {
        case 'xml':
          return new File([code], state.file.name, {
            type: 'text/xml',
          });
        case 'shell':
          return new File([code], state.file.name, {
            type: 'application/x-sh',
          });
        default:
          return new File([code], state.file.name, {
            type: 'text/plain;charset=utf-8',
          });
      }
    };

    try {
      const { subscript } = state;
      const formData = getFileFormData('files', getFile(mode));
      await fileManagerApis.uploadFiles(
        projectId,
        instanceId,
        subscript,
        path,
        formData
      );
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
      setTimeout(
        () =>
          navigate(state.previousLocation, {
            state: { ...state },
          }),
        800
      );
    } catch (error) {
      showToast(TOAST_TYPE.ERROR, t('updateFile', { ns: 'errors' }));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('requestedFile', { ns: 'instance' }),
          })
        )}
      />
    );

  return (
    instance && (
      <Container>
        <Breadcrumbs instanceName={instance.name} />
        <TitlePanel
          title={`${t('fileEditor', { ns: 'titles' })} - ${state.file.name}`}
        />
        <Editor mode={mode} code={code} onSubmit={onSubmit} />
      </Container>
    )
  );
};

export default SolrFileEditorPage;
